"use client";

import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { Link, useRouter } from "@/i18n/routing";
import { House, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { register, RegisterRequest, isAuthenticated } from "@/service/auth-service";
import { socialLogin, SocialProvider } from "@/service/social-auth-service";
import { useUserStore } from "@/store/store";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import CountryPicker from "@/components/country-picker";
import { type Country, getCountryIso2ForCities } from "@/lib/services/country-service";
import { TrainerIcon } from "@/components/icons";
import { BarbellsIcon } from "@/components/icons/barbells";
import SocialAuthButtons from "@/components/social-auth-buttons";
import PhoneInput from "@/components/phone-input";
import CityPicker from "@/components/city-picker";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export default function SignupPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [countryError, setCountryError] = useState("");
  const [selectedRole, setSelectedRole] = useState<"trainer" | "client" | null>(null);
  const [roleError, setRoleError] = useState("");
  /** Dial code for client signup (e.g. +20) — synced from PhoneInput */
  const [clientPhonePrefix, setClientPhonePrefix] = useState("");
  const router = useRouter();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const t = useTranslations("general");
  const tHome = useTranslations("home");
  const tValidation = useTranslations("validation");
  const { role, setRole, user, setUser, isAuthenticated: isUserAuthenticated, setIsAuthenticated } = useUserStore();
  const { toast } = useToast();

  // Get role from URL or store or local state
  const userRole = searchParams?.get("role") || role || selectedRole;
  const roleId = userRole === "trainer" ? 3 : 2; // 3 for trainer, 2 for client

  // Redirect authenticated users away from signup page
  useEffect(() => {
    if (typeof window === "undefined") return;
    
    // Check if user is authenticated (either via token or store)
    const hasToken = isAuthenticated();
    const isAuthenticatedUser = hasToken || isUserAuthenticated;
    
    if (isAuthenticatedUser && user) {
      // Redirect to appropriate dashboard based on role
      if (user.role_id === 2) {
        // Client - redirect to client dashboard
        router.push("/app/client/dashboard");
      } else if (user.role_id === 3) {
        // Trainer - redirect to trainer dashboard or wizard if profile incomplete
        if (user.is_profile_completed) {
          router.push("/app/trainer/dashboard");
        } else {
          router.push("/auth/trainer/signup-wizard");
        }
      } else {
        // Fallback - redirect to home
        router.push("/");
      }
    }
  }, [user, isUserAuthenticated, router]);

  // Initialize role from URL or store
  useEffect(() => {
    if (userRole && (userRole === "trainer" || userRole === "client")) {
      setSelectedRole(userRole as "trainer" | "client");
      setRole(userRole as "trainer" | "client");
    }
  }, [userRole, setRole]);

  // Create schema inside component — client role requires phone, city, DOB, gender
  const signupSchema = useMemo(() => {
    const base = {
      name: z.string().min(1, tValidation("name_required")),
      email: z
        .string()
        .email(tValidation("email_invalid"))
        .min(1, tValidation("email_required")),
      password: z.string().min(8, tValidation("password_min")),
      confirmPassword: z.string().min(8, tValidation("confirm_password_min")),
      agreeTerms: z.boolean().refine((val) => val === true, {
        message: tValidation("agree_terms"),
      }),
    };

    const clientExtras =
      selectedRole === "client"
        ? {
            phone: z
              .string()
              .min(8, tValidation("phone_required")),
            city: z.string().min(1, tValidation("city_required")),
            dateOfBirth: z.string().min(1, tValidation("dob_required")),
            gender: z.enum(["0", "1"], {
              required_error: tValidation("gender_required"),
            }),
          }
        : {
            phone: z.string().optional(),
            city: z.string().optional(),
            dateOfBirth: z.string().optional(),
            gender: z.string().optional(),
          };

    return z
      .object({
        ...base,
        ...clientExtras,
      })
      .refine((data) => data.password === data.confirmPassword, {
        message: tValidation("passwords_match"),
        path: ["confirmPassword"],
      });
  }, [selectedRole, tValidation]);

  type SignupFormData = z.infer<typeof signupSchema>;

  const form = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      agreeTerms: false,
      phone: "",
      city: "",
      dateOfBirth: "",
      gender: undefined,
    },
  });

  // Keep resolver in sync when role changes (client-only fields)
  useEffect(() => {
    form.clearErrors();
  }, [selectedRole, form]);

  const { mutate: handleSignup, isPending } = useMutation({
    mutationFn: async (data: SignupFormData) => {
      // Validate role
      if (!selectedRole) {
        const roleErrorMsg = t("select_role_to_continue") || "Please select a role";
        setRoleError(roleErrorMsg);
        throw new Error(roleErrorMsg);
      }
      setRoleError("");

      // Validate country
      if (!selectedCountry) {
        const countryErrorMsg = t("select_a_location_to_continue") || "Please select your country";
        setCountryError(countryErrorMsg);
        throw new Error(countryErrorMsg);
      }
      setCountryError("");

      // Prepare register request matching mobile app format
      const registerRequest: RegisterRequest = {
        name: data.name.trim(),
        email: data.email.trim(),
        password: data.password,
        password_confirmation: data.confirmPassword,
        country_name: selectedCountry.name.common,
        country_image_png: selectedCountry.flags.png,
        country_image_svg: selectedCountry.flags.svg,
        role_id: roleId,
        agree_terms: 1, // 1 for true, matching mobile app format
        early_access: 1, // Matching mobile app format
      };

      if (selectedRole === "client") {
        registerRequest.phone = (data as { phone?: string }).phone?.trim() || "";
        if (clientPhonePrefix) {
          registerRequest.phone_prefix = clientPhonePrefix;
        }
        registerRequest.city = (data as { city?: string }).city?.trim() || "";
        registerRequest.date_of_birth = (data as { dateOfBirth?: string }).dateOfBirth || "";
        const g = (data as { gender?: string }).gender;
        if (g === "0" || g === "1") {
          registerRequest.gender = parseInt(g, 10);
        }
      }

      return await register(registerRequest);
    },
    onSuccess: async (response) => {
      // After successful registration, send OTP email and redirect to OTP verification screen
      try {
        const { sendVerificationEmail } = await import("@/service/auth-service");

        // Get email and password from form data (password needed for auto-login after OTP verification)
        const email = form.getValues("email");
        const password = form.getValues("password");
        
        // Send OTP email
        await sendVerificationEmail(email, 'signup');

        toast({
          title: t("registration_successful") || "Success",
          description: t("otp_sent_to_email") || "Account created! Please check your email for the verification code.",
        });

        // Redirect to OTP verification screen with email and password in query params
        // Password is needed for auto-login after OTP verification
        const userRoleId = roleId || (selectedRole === "trainer" ? 3 : 2);
        setTimeout(() => {
          router.push(
            `/auth/verify-otp?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}&roleId=${userRoleId}`
          );
        }, 1500);
      } catch (error: any) {
        console.error("Failed to send OTP email:", error);
        const email = form.getValues("email");
        const password = form.getValues("password");
        const userRoleId = roleId || (selectedRole === "trainer" ? 3 : 2);
        const sendMsg =
          error?.message ||
          t("account_created_verify_email") ||
          "Account created. We could not send the email — use Resend on the next screen.";

        toast({
          title: t("registration_successful") || "Account created",
          description: sendMsg,
        });

        setTimeout(() => {
          router.push(
            `/auth/verify-otp?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}&roleId=${userRoleId}`
          );
        }, 1500);
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.message ||
        "Registration failed. Please try again.";
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: SignupFormData) => {
    // Validate role before submitting
    if (!selectedRole) {
      setRoleError(t("select_role_to_continue") || "Please select a role");
      return;
    }
    setRoleError("");

    // Validate country before submitting
    if (!selectedCountry) {
      setCountryError(t("select_a_location_to_continue") || "Please select your country");
      return;
    }
    setCountryError("");
    
    handleSignup(data);
  };

  // Social signup handler
  const { mutate: handleSocialSignup, isPending: isSocialSignupPending } = useMutation({
    mutationFn: async (provider: SocialProvider) => {
      // Validate role before social signup
      if (!selectedRole) {
        const roleErrorMsg = t("select_role_to_continue") || "Please select a role";
        setRoleError(roleErrorMsg);
        throw new Error(roleErrorMsg);
      }
      setRoleError("");

      return await socialLogin(provider, roleId);
    },
    onSuccess: (response) => {
      // Store user data
      if (response.user_data) {
        setUser(response.user_data);
        setIsAuthenticated(true);
      }

      // Handle navigation based on user role and profile completion
      const user = response.user_data;
      if (!user) {
        router.push("/");
        return;
      }

      // Check if this is a new account or existing account login
      // The backend returns the same response structure for both cases
      // We can determine if it's a new account by checking if profile is incomplete for trainers
      const isNewAccount = user.role_id === 3 && !user.is_profile_completed;

      if (isNewAccount) {
        toast({
          title: t("registration_successful") || "Success",
          description: t("account_created_successfully") || "Account created successfully!",
        });
      } else {
        // Existing account - user was automatically logged in
        toast({
          title: t("logged_in_successfully") || "Success",
          description: t("welcome_back") || "Welcome back!",
        });
      }

      if (user.role_id === 2) {
        // Client - redirect to client dashboard
        setTimeout(() => {
          router.push("/app/client/dashboard");
        }, 1500);
      } else if (user.role_id === 3) {
        // Trainer
        if (!user.is_profile_completed) {
          // Redirect to trainer wizard
          setTimeout(() => {
            router.push("/auth/trainer/signup-wizard");
          }, 1500);
        } else {
          // Trainer with completed profile
          if (!user.is_approved) {
            toast({
              title: t("profile_pending_approval") || "Pending Approval",
              description: t("profile_pending_approval") || "Your profile is pending approval.",
            });
          }
          setTimeout(() => {
            router.push("/app/trainer/dashboard");
          }, 1500);
        }
      } else {
        // Fallback
        setTimeout(() => {
          router.push("/");
        }, 1500);
      }
    },
    onError: (error: any) => {
      console.error("Social signup error:", error);
      
      // User closed the popup - don't show error, just silently fail
      if (
        error?.message?.includes("popup was closed") ||
        error?.message?.includes("popup-closed-by-user") ||
        error?.code === "auth/popup-closed-by-user"
      ) {
        return;
      }
      
      // Note: The backend automatically logs in existing users, so we shouldn't
      // see "account already exists" errors in normal flow. If we do, it's a real error.
      let errorMessage = error?.message || "Social signup failed. Please try again.";
      
      // Extract backend error message if available
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      }
      
      toast({
        title: t("error") || "Error",
        description: errorMessage,
        variant: "destructive",
        duration: 5000,
      });
    },
  });

  const handleGoogleSignup = () => {
    if (!selectedRole) {
      setRoleError(t("select_role_to_continue") || "Please select a role");
      return;
    }
    setRoleError("");
    handleSocialSignup(SocialProvider.GOOGLE);
  };

  const handleFacebookSignup = () => {
    if (!selectedRole) {
      setRoleError(t("select_role_to_continue") || "Please select a role");
      return;
    }
    setRoleError("");
    handleSocialSignup(SocialProvider.FACEBOOK);
  };

  return (
    <div className="relative flex flex-col justify-center items-center min-h-screen bg-black-bg px-4 py-8 sm:py-12">
      {/* Background Image */}
        <Image
          src="/signin-bg.webp"
          alt="Background"
          fill
          className="absolute z-10 w-full h-full object-cover"
          priority
        />
      
      {/* Home Button */}
      <Link
        href="/"
        className={cn(
          "absolute flex justify-center items-center top-5 text-primary-foreground w-12 h-12 p-3 bg-gradient-to-t from-primary to-second-primary rounded-full z-30",
          locale === "ar" ? "right-5" : "left-5"
        )}
      >
        <House className="w-6 h-6" />
      </Link>

      {/* Signup Card */}
      <Card className="mx-auto z-20 w-full max-w-xl border-[#595959] bg-black-bg text-primary-foreground rounded-2xl sm:rounded-3xl pt-8 sm:pt-16 px-4 sm:px-6 pb-6 sm:pb-8 max-h-[min(90vh,calc(100vh-4rem))] overflow-y-auto">
        <CardHeader className="justify-center items-center px-2 sm:px-6">
          <div className="flex justify-center items-center mb-4 sm:mb-6">
            <Image
              src="/logo.webp"
              alt="logo"
              width={160}
              height={160}
              className="w-[120px] sm:w-[160px] object-contain"
            />
          </div>
          <CardTitle className="text-4xl sm:text-5xl lg:text-6xl font-normal">
            <h1 className="lowercase">{t("signup_title") || "sign up"}</h1>
          </CardTitle>
          <CardDescription className="text-primary-foreground font-medium text-sm sm:text-base">
            {t("signup_description") || "Fill in your information to get started"}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-2 sm:px-6">
          <Form {...form}>
            <form
              className="grid gap-4"
              onSubmit={form.handleSubmit(onSubmit)}
              dir={locale === "ar" ? "rtl" : "ltr"}
            >
              {/* Role Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-primary-foreground/70">
                  {tHome("role_selection.title") || "I am a"}
                </label>
                <ToggleGroup
                  type="single"
                  value={selectedRole || undefined}
                  onValueChange={(value) => {
                    if (value) {
                      const role = value as "trainer" | "client";
                      setSelectedRole(role);
                      setRole(role);
                      setRoleError("");
                    }
                  }}
                  className={cn(
                    "grid grid-cols-1 sm:grid-cols-2 gap-4 w-full"
                  )}
                >
                  {locale === "ar" ? (
                    <>
                      <ToggleGroupItem
                        value="trainer"
                        className={cn(
                          "h-auto flex-col border border-[#595959] text-white hover:text-white transition-all hover:bg-primary/10 data-[state=on]:pointer-events-none data-[state=on]:bg-gradient-to-t data-[state=on]:from-primary data-[state=on]:to-second-primary data-[state=on]:text-white rounded-2xl min-h-24 font-medium py-4 px-4",
                          {
                            "opacity-50": selectedRole !== null && selectedRole !== "trainer",
                            "border-red-500": roleError && !selectedRole,
                          }
                        )}
                      >
                        <TrainerIcon
                          className={cn(
                            "fill-white !block !w-10 !h-10 mb-2 last-of-type:[&_path]:fill-black last-of-type:[&_path]:stroke-black",
                            {
                              "last-of-type:[&_path]:fill-primary last-of-type:[&_path]:stroke-primary":
                                selectedRole === "trainer",
                            }
                          )}
                        />
                        <span className="text-base">{tHome("role_selection.trainer_button") || "Trainer"}</span>
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        value="client"
                        className={cn(
                          "h-auto flex-col border border-[#595959] text-white hover:text-white transition-all hover:bg-primary/10 data-[state=on]:pointer-events-none data-[state=on]:bg-gradient-to-t data-[state=on]:from-primary data-[state=on]:to-second-primary data-[state=on]:text-white rounded-2xl min-h-24 font-medium py-4 px-4",
                          {
                            "opacity-50": selectedRole !== null && selectedRole !== "client",
                            "border-red-500": roleError && !selectedRole,
                          }
                        )}
                      >
                        <BarbellsIcon className="!w-10 !h-10 !block mb-2" />
                        <span className="text-base">{tHome("role_selection.client_button") || "Client"}</span>
                      </ToggleGroupItem>
                    </>
                  ) : (
                    <>
                      <ToggleGroupItem
                        value="client"
                        className={cn(
                          "h-auto flex-col border border-[#595959] text-white hover:text-white transition-all hover:bg-primary/10 data-[state=on]:pointer-events-none data-[state=on]:bg-gradient-to-t data-[state=on]:from-primary data-[state=on]:to-second-primary data-[state=on]:text-white rounded-2xl min-h-24 font-medium py-4 px-4",
                          {
                            "opacity-50": selectedRole !== null && selectedRole !== "client",
                            "border-red-500": roleError && !selectedRole,
                          }
                        )}
                      >
                        <BarbellsIcon className="!w-10 !h-10 !block mb-2" />
                        <span className="text-base">{tHome("role_selection.client_button") || "Client"}</span>
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        value="trainer"
                        className={cn(
                          "h-auto flex-col border border-[#595959] text-white hover:text-white transition-all hover:bg-primary/10 data-[state=on]:pointer-events-none data-[state=on]:bg-gradient-to-t data-[state=on]:from-primary data-[state=on]:to-second-primary data-[state=on]:text-white rounded-2xl min-h-24 font-medium py-4 px-4",
                          {
                            "opacity-50": selectedRole !== null && selectedRole !== "trainer",
                            "border-red-500": roleError && !selectedRole,
                          }
                        )}
                      >
                        <TrainerIcon
                          className={cn(
                            "fill-white !block !w-10 !h-10 mb-2 last-of-type:[&_path]:fill-black last-of-type:[&_path]:stroke-black",
                            {
                              "last-of-type:[&_path]:fill-primary last-of-type:[&_path]:stroke-primary":
                                selectedRole === "trainer",
                            }
                          )}
                        />
                        <span className="text-base">{tHome("role_selection.trainer_button") || "Trainer"}</span>
                      </ToggleGroupItem>
                    </>
                  )}
                </ToggleGroup>
                {roleError && (
                  <p className="text-xs text-red-500 font-poppins px-2">{roleError}</p>
                )}
              </div>

              {/* Name + email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl {...field}>
                        <Input
                          placeholder={t("name_placeholder") || "Your full name"}
                          className="bg-black rounded-full border-[#595959] text-primary-foreground py-5 sm:py-6 text-sm sm:text-base"
                        />
                      </FormControl>
                      <FormMessage className="text-start text-red-500" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl {...field}>
                        <Input
                          type="email"
                          placeholder={t("email_place_holder")}
                          className="bg-black rounded-full border-[#595959] text-primary-foreground py-5 sm:py-6 text-sm sm:text-base"
                        />
                      </FormControl>
                      <FormMessage className="text-start text-red-500" />
                    </FormItem>
                  )}
                />
              </div>

              {/* Country (+ city for clients) */}
              {selectedRole === "client" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:items-start">
                  <div className="min-w-0">
                    <CountryPicker
                      value={selectedCountry}
                      onChange={(country) => {
                        setSelectedCountry(country);
                        setCountryError("");
                        form.setValue("city", "");
                      }}
                      placeholder={t("location_place_holder") || "Select your location"}
                      error={countryError}
                      autoDetect={true}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem className="min-w-0">
                        <CityPicker
                          key={
                            getCountryIso2ForCities(selectedCountry) ??
                            selectedCountry?.name?.common ??
                            "no-country"
                          }
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={t("trainer.signup.city_placeholder")}
                          error={form.formState.errors.city?.message as string | undefined}
                          country={selectedCountry}
                          countryCode={selectedCountry?.cca2}
                          autoDetect={false}
                        />
                        <FormMessage className="text-start text-red-500" />
                      </FormItem>
                    )}
                  />
                </div>
              ) : (
                <div>
                  <CountryPicker
                    value={selectedCountry}
                    onChange={(country) => {
                      setSelectedCountry(country);
                      setCountryError("");
                      form.setValue("city", "");
                    }}
                    placeholder={t("location_place_holder") || "Select your location"}
                    error={countryError}
                    autoDetect={true}
                  />
                </div>
              )}

              {/* Client-only: phone, then DOB + gender */}
              {selectedRole === "client" && (
                <>
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <PhoneInput
                          value={field.value || ""}
                          onChange={field.onChange}
                          onPhoneChange={(_fullNumber, country) => {
                            const c = country as { phoneCode?: string };
                            setClientPhonePrefix(c.phoneCode || "");
                          }}
                          placeholder={t("trainer.signup.phone_placeholder")}
                          error={form.formState.errors.phone?.message as string | undefined}
                        />
                        <FormMessage className="text-start text-red-500" />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:items-end">
                    <FormField
                      control={form.control}
                      name="dateOfBirth"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              type="date"
                              aria-label={t("date_of_birth_placeholder")}
                              className="bg-black rounded-full border-[#595959] text-primary-foreground py-5 sm:py-6 text-sm sm:text-base [color-scheme:dark]"
                              {...field}
                              max={new Date(
                                new Date().setFullYear(new Date().getFullYear() - 13)
                              )
                                .toISOString()
                                .slice(0, 10)}
                            />
                          </FormControl>
                          <FormMessage className="text-start text-red-500" />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="gender"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className="flex flex-row flex-wrap gap-x-6 gap-y-2 items-center min-h-[3rem] sm:min-h-[3.25rem]"
                            >
                              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                                <RadioGroupItem value="1" id="gender-male" />
                                <Label htmlFor="gender-male" className="text-primary-foreground cursor-pointer font-normal">
                                  {t("trainer.signup.male")}
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                                <RadioGroupItem value="0" id="gender-female" />
                                <Label htmlFor="gender-female" className="text-primary-foreground cursor-pointer font-normal">
                                  {t("trainer.signup.female")}
                                </Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage className="text-start text-red-500" />
                        </FormItem>
                      )}
                    />
                  </div>
                </>
              )}

              {/* Password + confirm */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl {...field}>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder={t("password_placeholder") || "Password"}
                            className="bg-black rounded-full border-[#595959] text-primary-foreground py-5 sm:py-6 pr-10 sm:pr-12 text-sm sm:text-base"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? (
                              <EyeOff className="h-5 w-5" />
                            ) : (
                              <Eye className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage className="text-start text-red-500" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl {...field}>
                        <div className="relative">
                          <Input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder={t("confirm_password_placeholder") || "Confirm password"}
                            className="bg-black rounded-full border-[#595959] text-primary-foreground py-5 sm:py-6 pr-10 sm:pr-12 text-sm sm:text-base"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-5 w-5" />
                            ) : (
                              <Eye className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage className="text-start text-red-500" />
                    </FormItem>
                  )}
                />
              </div>

              {/* Terms and Conditions Checkbox */}
              <FormField
                control={form.control}
                name="agreeTerms"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="rounded-[0.25rem] mt-1"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <label className="text-sm font-poppins text-primary-foreground/70 cursor-pointer">
                        {t("agree_terms.agree") || "I agree to the"}{" "}
                        <Link
                          href="/terms-and-conditions"
                          className="text-primary hover:underline"
                          target="_blank"
                        >
                          {t("agree_terms.terms") || "Terms and Conditions"}
                        </Link>
                        {" "}{t("and") || "and"}{" "}
                        <Link
                          href="/privacy-policy"
                          className="text-primary hover:underline"
                          target="_blank"
                        >
                          {t("agree_terms.policy") || "Privacy Policy"}
                        </Link>
                      </label>
                    </div>
                  </FormItem>
                )}
              />

              {/* Sign Up Button */}
              <Button
                type="submit"
                className="w-full font-bebas rounded-full bg-gradient-to-r from-primary to-second-primary text-2xl sm:text-3xl py-5 sm:py-6 sm:pt-7"
                disabled={isPending || isSocialSignupPending}
              >
                {isPending
                  ? (t("loading") || "Loading...")
                  : (t("sign_up") || "sign up")}
              </Button>

              {/* Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-[#595959]" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-black-bg px-2 text-primary-foreground/70">
                    {t("or") || "Or"}
                  </span>
                </div>
              </div>

              {/* Social Auth Buttons */}
              <SocialAuthButtons
                onGoogleClick={handleGoogleSignup}
                onFacebookClick={handleFacebookSignup}
                isLoading={isSocialSignupPending}
                disabled={isPending || !selectedRole}
              />
            </form>
          </Form>

          <div className="mt-6 pt-5 border-t border-[#595959]/60 text-center px-1 shrink-0">
            <p className="text-sm text-primary-foreground/70 font-poppins leading-relaxed">
              <span>{t("already_have_account")}</span>
              {" "}
              <Link
                href="/auth/login"
                className="text-primary font-semibold hover:underline underline-offset-2"
              >
                {t("sign_in")}
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
